import React, { useState, useEffect } from "react";
import CircularLoader from "../../../CircularLoader/CircularLoader";
import { useTranslation } from "react-i18next";

const UploadDocumentModal = ({
  open,
  setOpen,
  onUpload,
  DOCUMENT_TYPE_MAP,
  uploadLoader,
}) => {
  const { t } = useTranslation("finances");
  const [documentType, setDocumentType] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [file, setFile] = useState(null);

  // ✅ Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setDocumentType("");
      setFile(null);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = () => {
    if (!documentType || !file || uploadLoader) return;

    onUpload({
      document_type: documentType,
      file,
    });
  };

  return (
    <div
      className="
        fixed inset-0 z-[9999]
        flex items-center justify-center
        bg-black/40 backdrop-blur-[3px]
        px-4
        animate-in fade-in duration-200
      "
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="
          w-full max-w-md
          rounded-2xl bg-ui-surface
          shadow-2xl border border-ui-surface-sunken
          overflow-hidden
          animate-in zoom-in-95 fade-in duration-200
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-ui-surface-sunken">
          <div>
            <h2 className="text-lg font-semibold text-ui-text">
              {t("invoice.documents.upload")}
            </h2>

            <p className="text-xs text-ui-text-muted mt-0.5">
              {t("invoice.documents.hint")}
            </p>
          </div>

          <button
            onClick={() => !uploadLoader && setOpen(false)}
            disabled={uploadLoader}
            className="
              h-8 w-8 rounded-full
              flex items-center justify-center
              text-ui-text-subtle hover:text-ui-text-muted
              hover:bg-ui-surface-sunken
              transition-colors
              disabled:opacity-50
            "
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Document Type */}
          <div className="relative">
            <label className="block text-sm font-medium text-ui-text mb-2">
              {t("invoice.documents.documentType")}
            </label>

            <button
              type="button"
              onClick={() => setDropdownOpen((p) => !p)}
              disabled={uploadLoader}
              className="
                w-full h-11 px-4
                rounded-xl border border-ui-border
                bg-ui-surface text-sm text-left
                flex items-center justify-between
                transition-all
                hover:border-ui-text-subtle
                focus:ring-4 focus:ring-brand-light
                disabled:bg-ui-surface-sunken
              "
            >
              <span
                className={documentType ? "text-ui-text" : "text-ui-text-subtle"}
              >
                {documentType
                  ? t(`invoice.documents.types.${documentType}`, {
                      defaultValue: DOCUMENT_TYPE_MAP[documentType].replaceAll(
                        "_",
                        " ",
                      ),
                    })
                  : t("invoice.documents.selectType")}
              </span>

              <span
                className={`
                  transition-transform duration-200
                  ${dropdownOpen ? "rotate-180" : ""}
                `}
              >
                ▼
              </span>
            </button>

            {dropdownOpen && (
              <div
                className="
                  absolute z-50 mt-2 w-full
                  rounded-2xl border border-ui-border
                  bg-ui-surface shadow-xl
                  overflow-hidden
                  animate-in fade-in zoom-in-95 duration-150
                "
              >
                <div className="max-h-64 overflow-auto py-1">
                  {Object.entries(DOCUMENT_TYPE_MAP).map(([key, label]) => {
                    const selected = documentType === key;

                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setDocumentType(key);
                          setDropdownOpen(false);
                        }}
                        className={`
                          w-full px-4 py-2.5 text-sm text-left
                          transition-all duration-150
                          flex items-center justify-between
                          ${
                            selected
                              ? "bg-brand-light text-brand-hover"
                              : "text-ui-text hover:bg-brand-light hover:text-brand-hover hover:pl-5"
                          }
                        `}
                      >
                        <span>
                          {t(`invoice.documents.types.${key}`, {
                            defaultValue: label.replaceAll("_", " "),
                          })}
                        </span>

                        {selected && <span>✓</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-ui-text mb-2">
              {t("invoice.documents.uploadFile")}
            </label>

            <label
              className="
                flex flex-col items-center justify-center
                w-full rounded-2xl border-2 border-dashed
                border-ui-border bg-ui-surface-sunken
                px-6 py-8
                text-center cursor-pointer
                transition-all duration-200
                hover:border-brand hover:bg-brand-light
              "
            >
              <input
                type="file"
                disabled={uploadLoader}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />

              <div className="text-3xl mb-2">📄</div>

              <p className="text-sm font-medium text-ui-text">
                {t("invoice.documents.clickToUpload")}
              </p>

              <p className="text-xs text-ui-text-muted mt-1">
                {t("invoice.documents.fileTypes")}
              </p>
            </label>

            {/* Selected File */}
            {file && (
              <div
                className="
                  mt-3 flex items-center gap-3
                  rounded-xl border border-state-success-border
                  bg-state-success-bg px-3 py-2
                "
              >
                <div className="text-state-success text-lg">✓</div>

                <div className="min-w-0">
                  <p className="text-sm font-medium text-ui-text truncate">
                    {file.name}
                  </p>

                  <p className="text-xs text-ui-text-muted">
                    {t("invoice.documents.ready")}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-ui-surface-sunken">
          <button
            onClick={() => !uploadLoader && setOpen(false)}
            disabled={uploadLoader}
            className="
              h-10 px-4 rounded-xl
              text-sm font-medium text-ui-text
              bg-ui-surface border border-ui-border
              hover:bg-ui-surface-sunken
              transition-colors
              disabled:opacity-50
            "
          >
            {t("common:actions.cancel")}
          </button>

          <button
            onClick={handleSubmit}
            disabled={!documentType || !file || uploadLoader}
            className="
              min-w-[150px] h-10 px-4 rounded-xl
              bg-brand text-white
              text-sm font-medium
              flex items-center justify-center gap-2
              transition-all duration-200
              hover:opacity-90
              disabled:opacity-60
              disabled:cursor-not-allowed
            "
          >
            {uploadLoader ? (
              <>
                <CircularLoader />
                {t("invoice.documents.uploading")}
              </>
            ) : (
              t("invoice.documents.upload")
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadDocumentModal;
